import { useEffect } from "react";
import { FormProvider, useForm } from "react-hook-form";

import { LookupIcon } from "@/assets/icons";
import FormField from "@/components/ui/formField";
import Modal from "@/components/ui/modal";
import ModalHeader from "@/components/ui/modal/modalHeader";

import { Button } from "equator-design-system";

const TEST_ID_PREFIX = "spatial-analysis-save-configuration-popup";

export interface ConfigurationFormData {
  id?: string;
  name: string;
  description: string;
}

type SaveConfigurationPopupProps = {
  show: boolean;
  nameIsExist: boolean;
  setNameIsExist: (value: boolean) => void;
  onClose: () => void;
  onSave: (data: ConfigurationFormData) => void;
};

const SaveConfigurationPopup = ({
  show,
  nameIsExist = false,
  setNameIsExist,
  onClose,
  onSave
}: SaveConfigurationPopupProps) => {
  const form = useForm<ConfigurationFormData>({
    defaultValues: {
      name: "",
      description: ""
    }
  });
  const {
    register,
    handleSubmit,
    setValue,
    watch,
    formState: { errors, isValid }
  } = form;
  const nameWatch = watch("name");

  const onSubmit = (data: ConfigurationFormData) => {
    onSave(data);
  };

  useEffect(() => {
    if (!nameIsExist) return;
    setNameIsExist(false);
  }, [nameIsExist, nameWatch, setNameIsExist]);

  return (
    <Modal isOpen={show} onClose={onClose} containerClassname="w-[432px] h-fit">
      <FormProvider {...form}>
        <form onSubmit={handleSubmit(onSubmit)}>
          <ModalHeader
            title="Save Configuration"
            onClose={onClose}
            titleIcon={
              <div className="w-7 h-7 bg-teal-50 rounded-lg flex-center">
                <LookupIcon className="fill-teal-500 w-4" />
              </div>
            }
          />
          <div className="flex flex-col mt-2 p-4 gap-4">
            <FormField<ConfigurationFormData>
              name="name"
              register={register}
              rules={{ required: true }}
              placeHolder="Configuration name "
              resetValue={() => setValue("name", "")}
              error={nameIsExist ? "Name already exists" : errors.name?.message}
            />
            <FormField<ConfigurationFormData>
              name="description"
              isAreaInput
              register={register}
              rules={{ required: true }}
              placeHolder="Description"
              resetValue={() => setValue("description", "")}
              error={errors.description?.message}
            />
          </div>
          <div className="flex px-4 gap-2 w-full pb-3 rounded-t-none rounded-lg">
            <Button
              variant="secondary"
              className="h-[40px] w-1/2 btn-secondary text-sm !font-bold"
              onClick={onClose}
            >
              <span>Cancel</span>
            </Button>
            <Button
              className="h-[40px] w-1/2 btn-primary text-sm !font-bold"
              type="submit"
              disabled={!isValid || nameIsExist}
              data-testid={`${TEST_ID_PREFIX}-save-button`}
            >
              <span>Save</span>
            </Button>
          </div>
        </form>
      </FormProvider>
    </Modal>
  );
};

export default SaveConfigurationPopup;
